// The in-app tutorial (REPORT-tutorial.md): the first-login tour and the Getting started checklist. Plain JS, no React and no
// Supabase import, so tests/tutorial.test.mjs can check every role. The tour draws what tourSteps() returns; the card what jobsFor()
// returns. Who sees a page is never decided here: visibleItems() (src/lib/navGroups.js) and canSignMaster / memberCan are the
// existing rules. Wording follows the Hey Captain Guide v4 (src/lib/guideHelp.json, entry id in `guide`).
import { visibleItems } from './navGroups.js'
import { canSignMaster, MASTER_ROLES } from './permModules.js'
import { memberCan } from './companyMembers.js'

// ---------- who is this login? ----------
export function tourRole({ accountType, role } = {}) {
  if (accountType === 'company') return 'management'
  if (accountType !== 'crew' || role === 'captain') return 'captain'
  if (role === 'engineer' || role === 'chief_engineer') return 'engineer'
  if (role === 'chief_officer' || role === 'safety_officer' || role === 'officer') return 'officer'
  return 'crew'
}
export const ROLE_TITLE = { management: 'management', captain: 'the Captain', engineer: 'engineers', officer: 'officers', crew: 'crew' }

// a browser driven by a test tool (Playwright's navigator.webdriver) or a run that asks for it: no tour, no card, no calls.
// The API has no screen at all, so an API login never gets here. Our own tutorial tests switch it back on with the storage key.
export const TEST_KEY = 'hc_tutorial_test'
export function isAutomated({ nav, search = '', storage } = {}) {
  try { if (storage && storage.getItem(TEST_KEY) === '1') return false } catch (e) {}
  return !!(nav && nav.webdriver) || /[?&]hc_no_tour\b/.test(search)
}

// ---------- the tour ----------
const nav = page => '[data-nav="' + page + '"]'
// the items of one role step: only pages this login can open (`page`), or a right it has (`show`)
const ROLE_STEPS = {
  crew: [
    { id: 'crew-hours', guide: 'crew-hor', title: 'Your hours of rest', items: [
      { page: 'hours_rest', text: 'Record your work and rest, month by month. The page checks the rest rules as you fill it in. You can also tell the assistant your hours from the phone.' }] },
    { id: 'crew-own', guide: 'crew-mydetails', title: 'My Details and the Crew Board', items: [
      { page: 'my_details', text: '**My Details**: keep your own phone, e-mail and emergency contact up to date.' },
      { page: 'announcements', text: '**Crew Board**: the vessel\'s notice board, with safety notices, drills and orders.' }] },
  ],
  officer: [
    { id: 'officer-safety', guide: 'safety-checklists-run', title: 'Your safety pages', items: [
      { page: 'checklists', text: '**Checklists**: run them step by step on any device, signed and kept with a PDF.' },
      { page: 'lsa', text: '**LSA / FFE**: the life-saving and fire-fighting register, with a quick monthly check on every item.' },
      { page: 'permits', text: '**Permits to Work**: the Job Card takes you through Brief, Check and Live.' }] },
    { id: 'officer-logs', guide: 'maintenance-tasks', title: 'Logs and maintenance', items: [
      { page: 'logbooks', text: '**Log Books**: the deck log, engine room log and Oil Record Book.' },
      { page: 'mtask', text: '**Maintenance Tasks**: planned maintenance, showing when each job is next due.' },
      { show: 'master', page: 'hours_rest', text: '**Sign as Master**: on Hours of Rest you sign the month for the crew.' }] },
  ],
  engineer: [
    { id: 'engineer-machinery', guide: 'maintenance-tasks', title: 'Maintenance and engine room', items: [
      { page: 'mtask', text: '**Maintenance Tasks**: planned maintenance by date or running hours.' },
      { page: 'mlog', text: '**Maintenance Log**: the record of work done.' },
      { page: 'logbooks', text: '**Log Books**: the Engine Room Log, the daily sheet, and the Oil Record Book.' }] },
    { id: 'engineer-safety', guide: 'inventory', title: 'Stock, checklists and permits', items: [
      { page: 'inventory', text: '**Inventory**: spare parts and stock levels.' },
      { page: 'checklists', text: '**Checklists**: run them step by step, signed and kept with a PDF.' },
      { page: 'lsa', text: '**LSA / FFE**: the safety equipment register and its monthly checks.' },
      { page: 'permits', text: '**Permits to Work**: the Job Card for hot work, enclosed spaces and other risky jobs.' }] },
  ],
  captain: [
    { id: 'captain-logins', guide: 'crew-logins', title: 'Your crew and their logins', items: [
      { page: 'crew_logins', text: '**Crew Logins**: give each crew member their own login and decide, area by area, what they can see and change.' },
      { page: 'crew', text: '**Crew**: the crew list, certificates and documents.' }] },
    { id: 'captain-approve', guide: 'po-approval-card', title: 'Approvals and Sign as Master', items: [
      { page: 'purchase_orders', text: '**Waiting for your approval**: orders and requests that need you appear in one card at the top of the Dashboard.' },
      { show: 'master', page: 'hours_rest', text: '**Sign as Master**: on Hours of Rest you sign each crew member\'s month.' }] },
  ],
}
// management works from the Fleet page, whose menu holds only Fleet and the assistant: these point at the Fleet page itself
const FLEET_STEPS = [
  { id: 'fleet-vessels', guide: 'fleet-open', title: 'Your vessels', target: '[data-tour="fleet-vessels"]', text: 'Open a vessel to work inside it: you see its full menu, as its Captain would. A bar at the top switches to another vessel or takes you back to the Fleet.' },
  { id: 'fleet-approve', guide: 'fleet-report', title: 'Approvals and reports', target: '[data-tour="fleet-report"]', items: [
    { show: 'approvals', text: '**Waiting for your approval**: every order and APA close request that needs you, on all your vessels.' },
    { show: 'reports', text: '**Fleet report**: a monthly PDF for the office, or one owner report per vessel.' }] },
]
const bulletsOf = (items, allow) => (items || []).filter(allow).map(i => i.text)

// auth = { accountType, role, config, selectedVessel, can, hasFeature, isCaptain }; opts = { aiAllowed, standalone, canSeeAllHistory }
export function tourSteps(auth, { aiAllowed = false, standalone = false, canSeeAllHistory } = {}) {
  const role = tourRole(auth)
  const atFleet = role === 'management'
  // what the login sees on the page the tour runs on: for management the Fleet page, even when a vessel is open right now
  const visible = visibleItems(atFleet ? { ...auth, selectedVessel: null } : auth, canSeeAllHistory)
  const sees = id => visible.some(n => n.id === id)
  const perms = auth.config && auth.config.permissions
  const master = auth.accountType === 'vessel' || (auth.accountType === 'crew' && canSignMaster(auth.role, perms))
  const member = auth.config && auth.config.member
  const rights = (member && member.rights) || {}
  const allowed = item => {
    if (item.page && !sees(item.page)) return false
    if (item.show === 'master') return master
    if (item.show === 'approvals') return !member || rights.approvals === 'full'
    if (item.show === 'reports') return !member || memberCan(member, 'reports')
    return true
  }
  const out = []
  out.push(atFleet
    ? { id: 'menu', guide: 'fleet-dashboard', title: 'Welcome aboard Hey Captain', target: '.hc-sidebar', menu: true, text: 'This is your menu. **Fleet** is one screen for all your vessels; **AI Assistant** answers across them. Open a vessel and you get its full menu here.' }
    : { id: 'menu', guide: 'start-dashboard', title: 'Welcome aboard Hey Captain', target: '.hc-sidebar', menu: true, text: 'This is your menu, grouped by area. It shows only the pages your login can use. The vessel\'s name at the top tells you which yacht you are on.' })
  if (atFleet) out.push({ id: 'status', guide: 'fleet-dashboard', title: 'The Fleet page', target: '[data-tour="fleet-tabs"]', text: 'One screen for all your vessels: totals, a map, readiness per vessel, approvals and alerts. The tabs here open the other fleet views.' })
  else if (sees('dashboard')) out.push({ id: 'status', guide: 'start-dashboard', title: 'Vessel status', target: '.dv-status', text: 'The Dashboard shows what needs attention today. Each tile is one area, in green, orange or red. Tap a tile to open that page.' })
  if (aiAllowed) out.push({ id: 'ai', guide: 'start-ai', title: 'Ask the assistant', target: '.hc-fab', text: 'Ask anything about your vessel, in plain words, by typing or by voice. Or say **"do it for me"**: it makes a card, and nothing is saved until you press **Approve**.' })
  out.push({ id: 'help', guide: 'start-dashboard', title: 'Help on every page', target: '.hc-guide-btn', text: 'The **?** at the top opens the Hey Captain guide for the page you are on: who can use it, the steps and tips.' })
  out.push({ id: 'phone', guide: standalone ? 'start-offline' : 'start-install', title: standalone ? 'Works without signal' : 'On your phone, even without signal', target: null,
    text: (standalone ? '' : '**iPhone**: in Safari tap Share, then Add to Home Screen.\n**Android**: in Chrome tap the ⋮ menu, then Install app.\n') + 'At sea or in the engine room the common jobs keep working. Your changes are sent by themselves when the signal is back.' })
  for (const s of atFleet ? FLEET_STEPS : ROLE_STEPS[role]) {
    if (s.text) { out.push({ ...s }); continue }
    const use = s.items.filter(allowed)
    if (!use.length) continue
    const first = use.find(i => i.page)
    // only one page left of several: the title names that page, never one the login cannot open
    const only = use.length === 1 && s.items.length > 1 && richParts(use[0].text).find(x => x.bold)
    out.push({ id: s.id, guide: s.guide, title: only ? only.text : s.title, bullets: use.length > 1 ? use.map(i => i.text) : null, text: use.length > 1 ? '' : use[0].text,
      target: s.target || (first ? nav(first.page) : null), navPage: first && first.page, menu: !!first })
  }
  return out
}
// **label** -> [{ text, bold }]: the tour and the card draw their own bold, no HTML
export const richParts = s => String(s || '').split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map(p => (/^\*\*[^*]+\*\*$/.test(p) ? { text: p.slice(2, -2), bold: true } : { text: p, bold: false }))

// where the step card goes: beside the lit element when there is room, else above or below it, always on screen
export function cardPosition(rect, card, view, gap = 16) {
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, Math.max(lo, hi)))
  const cx = (view.w - card.w) / 2, cy = (view.h - card.h) / 2
  if (!rect) return { left: cx, top: cy, side: 'center' }
  const right = rect.left + rect.width + gap, left = rect.left - card.w - gap, below = rect.top + rect.height + gap, above = rect.top - card.h - gap
  const midX = clamp(rect.left + rect.width / 2 - card.w / 2, 12, view.w - card.w - 12), midY = clamp(rect.top + rect.height / 2 - card.h / 2, 12, view.h - card.h - 12)
  if (right + card.w <= view.w - 12) return { left: right, top: midY, side: 'right' }
  if (left >= 12) return { left, top: midY, side: 'left' }
  if (below + card.h <= view.h - 12) return { left: midX, top: below, side: 'below' }
  if (above >= 12) return { left: midX, top: above, side: 'above' }
  return { left: cx, top: cy, side: 'center' }
}
// the phone's bottom sheet moves to the top when it would cover what it talks about (the round AI button sits at the bottom)
export const sheetAtTop = (rect, sheetH, viewH) => !!rect && rect.height < viewH - sheetH - 24 && rect.top + rect.height > viewH - sheetH - 8

// ---------- the Getting started checklist ----------
// DO_IT key (src/lib/guideSearch.js) = the normal chat message of the "Do it for me" button; `module` = the right that job needs.
// A job with `mark` ticks when the login does it (opens the page, installs the app); with `probe` when its record is there.
const JOB = {
  install: { label: 'Put Hey Captain on your phone', guide: 'start-install', mark: 'install', always: true },
  my_details: { label: 'Check My Details', page: 'my_details', guide: 'crew-mydetails', mark: 'my_details' },
  hours_today: { label: 'Log today\'s hours of rest', page: 'hours_rest', guide: 'crew-hor', probe: 'hours_today', ai: 'Do it for me: record my hours of rest for today', module: 'hours_rest', level: 'read' },
  board: { label: 'Read a Crew Board notice', page: 'announcements', guide: 'regs-board', mark: 'board' },
  ai_card: { label: 'Approve your first AI card', guide: 'start-ai', probe: 'ai_card', needsAi: true, ai: 'Do it for me: add a To Do / snag', module: 'todo' },
  todo: { label: 'Open your To Do list', page: 'todo', guide: 'todo', mark: 'todo' },
  add_crew: { label: 'Add your crew', page: 'crew', guide: 'crew-list', probe: 'add_crew', ai: 'Do it for me: add a new crew member', module: 'crew' },
  certificate: { label: 'Upload a certificate', page: 'crew_certs', visiblePage: 'crew', guide: 'crew-certs', probe: 'certificate', ai: 'Do it for me: add a crew certificate', module: 'crew' },
  crew_login: { label: 'Create a crew login', page: 'crew_logins', guide: 'crew-logins', probe: 'crew_login' },
  checklist: { label: 'Run a checklist', page: 'checklists', guide: 'safety-checklists-run', probe: 'checklist', ai: 'Do it for me: run a checklist', module: 'checklists' },
  maintenance: { label: 'Log running hours or maintenance', page: 'mlog', guide: 'maintenance-log', probe: 'maintenance', ai: 'Do it for me: log maintenance work', module: 'maintenance' },
  engine_log: { label: 'Make an engine room log entry', page: 'logbooks', guide: 'engine-room-log', probe: 'engine_log', ai: 'Do it for me: fill in today\'s engine room log', module: 'logbooks' },
  stock: { label: 'Check your stock', page: 'inventory', guide: 'inventory', mark: 'stock' },
  lsa: { label: 'Record an LSA / FFE check', page: 'lsa', guide: 'safety-lsa', probe: 'lsa', ai: 'Do it for me: record the LSA / FFE check', module: 'lsa' },
  permit: { label: 'Start a permit to work', page: 'permits', guide: 'safety-job-card', probe: 'permit', ai: 'Do it for me: start a permit to work', module: 'ptw' },
  open_vessel: { label: 'Open a vessel', fleet: true, guide: 'fleet-open', mark: 'open_vessel' },
  compliance: { label: 'Check the fleet compliance list', fleet: 'compliance', guide: 'fleet-compliance', mark: 'compliance', show: 'compliance' },
  fleet_report: { label: 'Make a fleet report', fleet: true, guide: 'fleet-report', mark: 'fleet_report', show: 'reports' },
}
export const JOB_IDS = Object.keys(JOB)
const JOBS_OF = {
  crew: ['install', 'my_details', 'hours_today', 'board', 'ai_card', 'todo'],
  officer: ['install', 'checklist', 'lsa', 'permit', 'hours_today', 'ai_card'],
  engineer: ['install', 'maintenance', 'engine_log', 'stock', 'hours_today', 'ai_card'],
  captain: ['install', 'add_crew', 'certificate', 'crew_login', 'ai_card', 'checklist'],
  management: ['install', 'open_vessel', 'compliance', 'fleet_report', 'ai_card'],
}
// the Chief Officer stands in for the Captain: the Captain's setup jobs (Crew Logins stays out by its own rule)
export const jobsRole = auth => (auth.accountType === 'crew' && MASTER_ROLES.includes(auth.role) ? 'captain' : tourRole(auth))

export function jobsFor(auth, { aiAllowed = false, canSeeAllHistory } = {}) {
  const role = jobsRole(auth)
  const atFleet = role === 'management'
  const visible = visibleItems(atFleet ? { ...auth, selectedVessel: null } : auth, canSeeAllHistory)
  const sees = id => visible.some(n => n.id === id)
  const member = auth.config && auth.config.member
  const ok = id => {
    const j = JOB[id]
    if (j.needsAi && !aiAllowed) return false
    if (j.show === 'reports') return !member || memberCan(member, 'reports')
    if (j.show === 'compliance') return !member || memberCan(member, 'documents')
    return j.always || j.fleet || j.needsAi || sees(j.visiblePage || j.page)
  }
  // a login with Edit (or, for the own hours of rest, View) sees "Do it with AI"; the server checks again when the card is made
  // not on the Fleet page: no vessel is open there for the card to act on
  const mayAi = j => !atFleet && aiAllowed && !!j.ai && (!auth.can || auth.can(j.module, j.level || 'write'))
  return JOBS_OF[role].filter(ok).filter(id => id !== 'todo' || !aiAllowed).slice(0, 6).map(id => ({ id, ...JOB[id], ai: mayAi(JOB[id]) ? JOB[id].ai : null }))
}

// One read per job, one row at most. Rows carrying a name or login count only for this login; a setup job (Captain) counts the
// vessel's rows; a job on a shared log with no name on it counts rows made since this login's checklist began (ctx.since).
export function probeQuery(id, { uid, vesselId, name, since, today, role } = {}) {
  const cl = { client_id: vesselId }
  switch (id) {
    case 'ai_card': return { table: 'ai_proposals', select: 'id', eq: { user_id: uid, status: 'executed' } }
    // rows are kept by the crew record's name, which may not be spelled exactly as the login's: compared loosely, as api/_agent.js does
    case 'hours_today': return name ? { table: 'hours_rest', select: 'crew_member,days', eq: { ...cl, month: String(today || '').slice(0, 7) }, test: rows => hoursLogged((rows || []).filter(r => sameName(r.crew_member, name)), today) } : null
    case 'add_crew': return { table: 'crew', select: 'id', eq: cl }
    case 'certificate': return { table: 'crew_docs', select: 'id', eq: cl }
    case 'crew_login': return { table: 'crew_accounts', select: 'id', eq: cl }
    case 'checklist': return { table: 'checklist_instances', select: 'id', eq: cl, notNull: 'completed_at', ...(role === 'captain' ? {} : { gte: { started_at: since } }) }
    case 'maintenance': return [{ table: 'maintenance_log', select: 'id', eq: cl, gte: { created_at: since } }, { table: 'running_log', select: 'id', eq: cl, gte: { created_at: since } }]
    case 'engine_log': return { table: 'engine_log', select: 'id', eq: cl, gte: { updated_at: since } }
    case 'lsa': return name ? { table: 'lsa_checks', select: 'id', eq: { checked_by: name } } : null
    case 'permit': return { table: 'permits', select: 'id', eq: cl, gte: { created_at: since } }
    default: return null
  }
}
const nrm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '')
const sameName = (a, b) => { const x = nrm(a), y = nrm(b), w = String(b || '').trim().split(/\s+/).map(nrm).filter(Boolean); return !!x && (x === y || (w.length > 1 && w.every(p => x.includes(p)))) }
const hoursLogged = (rows, today) => (rows || []).some(r => {
  try { const d = JSON.parse(r.days || '{}')[today]; return !!d && (d.blocks || []).some(Boolean) } catch (e) { return false }
})
// a read that fails (no right, offline, table not there) just leaves the job unticked
export async function runProbe(client, q) {
  if (!q) return false
  if (Array.isArray(q)) { for (const one of q) if (await runProbe(client, one)) return true; return false }
  if (Object.values(q.gte || {}).some(v => !v)) return false   // "since when" not known yet: never count someone else's old rows
  let b = client.from(q.table).select(q.select)
  for (const [k, v] of Object.entries(q.eq || {})) b = b.eq(k, v)
  for (const [k, v] of Object.entries(q.gte || {})) b = b.gte(k, v)
  if (q.notNull) b = b.not(q.notNull, 'is', null)
  const { data, error } = await b.limit(q.test ? 100 : 1)
  if (error) return false
  return q.test ? q.test(data) : (data || []).length > 0
}

// pages whose opening is the job ("check", "read"), and what opens them
export const PAGE_MARK = { my_details: 'my_details', announcements: 'board', inventory: 'stock', todo: 'todo' }
export const isDone = (job, marks) => !!(marks && marks[job.id])

// ---------- the saved state, per login ----------
// { tour_done, checklist_hidden, checklist_done, marks: { <job>: when, started: when } } in public.tutorial_state (sql/tutorial.sql),
// with a copy on this device. Until the SQL is run the copy is all there is: the tour shows once on each device, nothing breaks.
export const emptyState = () => ({ tour_done: false, checklist_hidden: false, checklist_done: false, marks: {} })
export const localKey = uid => 'hc_tutorial:' + uid
const isMissingTable = error => !!error && (error.code === '42P01' || error.code === 'PGRST205' || error.status === 404 || /tutorial_state|schema cache|does not exist/i.test(error.message || ''))
const pick = r => ({ tour_done: !!r.tour_done, checklist_hidden: !!r.checklist_hidden, checklist_done: !!r.checklist_done, marks: r.marks && typeof r.marks === 'object' ? r.marks : {} })

// what is shown when this device and the database differ: a change not sent yet wins; the tour stays done and ticks stay ticked
export function mergeState(local, server, dirty) {
  if (!server) return local
  const base = dirty ? local : server
  return { ...base, tour_done: server.tour_done || local.tour_done, marks: { ...server.marks, ...local.marks } }
}

export function createTutorialStore({ client, uid, storage }) {
  let state = emptyState(), loaded = false, canSave = true, dirty = false, loading = null
  const subs = new Set()
  const read = () => { try { const v = JSON.parse(storage.getItem(localKey(uid)) || 'null'); if (v) { state = { ...emptyState(), ...pick(v.state || {}) }; dirty = !!v.dirty } } catch (e) {} }
  const keep = () => { try { storage.setItem(localKey(uid), JSON.stringify({ state, dirty })) } catch (e) {} }
  const tell = () => subs.forEach(f => f(state))
  async function send() {
    if (!canSave) return
    const { error } = await client.from('tutorial_state').upsert({ user_id: uid, ...state, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
    if (!error) { dirty = false; keep() } else if (isMissingTable(error)) canSave = false   // SQL not run yet: the copy on this device is enough
  }
  read()
  return {
    get: () => state,
    get loaded() { return loaded },
    subscribe(f) { subs.add(f); return () => subs.delete(f) },
    load() { loading = loading || doLoad(); return loading },
    // a change made before the login's saved state is back waits for it, so a tick on a new device cannot overwrite "hidden" or "done"
    async set(patch) {
      if (loading) await loading
      state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) }; dirty = true; keep(); tell()
      return send().catch(() => {})
    },
    async mark(keys) {
      if (loading) await loading
      const list = (Array.isArray(keys) ? keys : [keys]).filter(k => !state.marks[k])
      if (!list.length) return undefined
      const now = new Date().toISOString()
      return this.set(st => ({ marks: { ...st.marks, ...Object.fromEntries(list.map(k => [k, now])) } }))
    },
  }
  async function doLoad() {
      try {
        const { data, error } = await client.from('tutorial_state').select('tour_done,checklist_hidden,checklist_done,marks').eq('user_id', uid).maybeSingle()
        if (error) { if (isMissingTable(error)) canSave = false }
        else {
          state = mergeState(state, data ? pick(data) : null, dirty)
          keep()
          if (dirty || (!data && (state.tour_done || Object.keys(state.marks).length))) { dirty = true; await send() }
        }
      } catch (e) { /* offline: this device's copy */ }
      loaded = true
      tell()
      return state
  }
}
// show the tour by itself? Only once, never for a test run or the admin login, never over a link from an e-mail or an emergency run
export function shouldAutoStart({ state, automated, config, deepLink, emergency }) {
  if (automated || deepLink || emergency) return false
  if (!config || config.is_admin || config.accountType === 'admin') return false
  return !state.tour_done
}
